import { type ReactNode, useCallback, useEffect, useState } from "react";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { PortalCollectionApi } from "@/features/portal/api";

/** A collection record is a raw snake_case object; it always has an `id`. */
export type PortalRecord = Record<string, unknown> & { id: string };

export interface Column<T> {
  header: string;
  cell: (row: T) => ReactNode;
  className?: string;
}

/**
 * Loads (and reloads) a portal collection. Optionally passes a query string
 * (e.g. `belongs_to=TAXPAYER`). Client-side session scopes to the bound client.
 */
export function usePortalCollection<T extends PortalRecord>(
  api: PortalCollectionApi<T>,
  query?: string,
) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    setLoading(true);
    return api
      .list(query)
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .list(query)
      .then((data) => active && setRows(Array.isArray(data) ? data : []))
      .catch(() => active && setRows([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return { rows, loading, reload };
}

interface CollectionCardProps<T extends PortalRecord> {
  title: string;
  description?: string;
  addLabel?: string;
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  emptyText?: string;
  onAdd: () => void;
  onEdit: (row: T) => void;
  onDelete: (row: T) => Promise<void>;
  /** Called after a successful delete so the parent can refresh the list. */
  onDeleted?: () => void | Promise<void>;
}

/**
 * Portal-scoped list surface for a limited-field 433 collection: an
 * edge-to-edge Card with a header + Add button, a row per item, and per-row
 * Edit/Delete (delete behind a confirm). Self-contained (does not import the
 * staff worksheets components).
 */
export function CollectionCard<T extends PortalRecord>({
  title,
  description,
  addLabel = "Add",
  columns,
  rows,
  loading,
  emptyText = "Nothing added yet.",
  onAdd,
  onEdit,
  onDelete,
  onDeleted,
}: CollectionCardProps<T>) {
  const [pendingDelete, setPendingDelete] = useState<T | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await onDelete(pendingDelete);
      await onDeleted?.();
      toast.success(`${title.replace(/s$/, "")} removed`);
      setPendingDelete(null);
    } catch {
      toast.error("Couldn't delete — try again.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div>
          <h3 className="font-semibold">{title}</h3>
          {description && <p className="text-muted-foreground text-sm">{description}</p>}
        </div>
        <Button type="button" size="sm" variant="outline" onClick={onAdd}>
          <FiPlus className="size-4" /> {addLabel}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((c) => (
              <TableHead key={c.header} className={c.className}>
                {c.header}
              </TableHead>
            ))}
            <TableHead className="w-20 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + 1}
                className="text-muted-foreground py-6 text-center"
              >
                {loading ? "Loading…" : emptyText}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.id}>
                {columns.map((c) => (
                  <TableCell key={c.header} className={c.className}>
                    {c.cell(row)}
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label="Edit"
                      onClick={() => onEdit(row)}
                    >
                      <FiEdit2 className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label="Delete"
                      onClick={() => setPendingDelete(row)}
                    >
                      <FiTrash2 className="text-destructive size-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this item?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
